import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { FileIcon } from '@components/FileIcon';
import { IconButton } from '@components/buttons';
import { fonts } from '@theme/fontsCustomer';
import { DownloadIcon } from '@icons/DownloadIcon';
import { EyePreviewIcon } from '@icons/EyePreviewIcon';
import { XIcon } from '@icons/XIcon';
import type { FileAttachment, FileMeta } from '@customHooks/useFileDialog';

type AttachmentFileProps = {
  attachment?: FileAttachment;
  dummyFile?: FileMeta;
  isDummy?: boolean;
  enabledDeleteFile?: boolean;
  enabledDownload?: boolean;
  showPdfPreviewFn?: (fileId: string) => void;
  deleteFileFn?: (fileId: string) => void;
  downloadFileFn?: (fileId: string) => Promise<void>;
};

export const AttachmentFile = ({
  attachment,
  dummyFile,
  enabledDeleteFile = false,
  isDummy = false,
  enabledDownload = false,
  showPdfPreviewFn,
  deleteFileFn,
  downloadFileFn,
}: AttachmentFileProps) => {
  const { colors } = useTheme();

  if (isDummy) {
    const fileName = dummyFile?.fileName || '';

    return (
      <Attachment key={dummyFile?.fileId} $isDummy>
        <FileInfo $isDummy>
          <FileIcon fileExtension="" />
          <FileName>{fileName}</FileName>
        </FileInfo>
      </Attachment>
    );
  }

  const fileExtension = attachment?.fileExtension || '';
  const fileId = attachment?.fileId || '';
  const isPdf = fileExtension === 'pdf';

  const handleClickAttachment = () => {
    if (isPdf && typeof showPdfPreviewFn === 'function') {
      showPdfPreviewFn(fileId);
    }
  };

  return (
    <Attachment key={fileId} onClick={handleClickAttachment}>
      <FileInfo>
        <FileIcon fileExtension={fileExtension} />
        <FileName>{`${attachment?.fileName}${fileExtension && `.${fileExtension}`}`}</FileName>
      </FileInfo>
      <ButtonWrapper>
        {isPdf && showPdfPreviewFn && (
          <IconButton variant="icon" color="primary" onClick={() => showPdfPreviewFn(fileId)}>
            <EyePreviewIcon width={16} height={16} />
          </IconButton>
        )}
        {enabledDownload && (
          <IconButton
            variant="icon"
            color="primary"
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              if (typeof downloadFileFn === 'function') {
                downloadFileFn(fileId);
              }
            }}
          >
            <DownloadIcon width={16} height={16} />
          </IconButton>
        )}
        {enabledDeleteFile && (
          <IconButton
            variant="icon"
            color="primary"
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              if (typeof deleteFileFn === 'function') {
                deleteFileFn(fileId);
              }
            }}
          >
            <XIcon width={14} height={14} color={colors['ic-gray-main']} />
          </IconButton>
        )}
      </ButtonWrapper>
    </Attachment>
  );
};

const Attachment = styled.div<{ $isDummy?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 20px;
  box-sizing: content-box;
  border: 1px solid ${({ theme: { colors } }) => colors['border-gray-lighter']};
  border-radius: 5px;
  padding: ${({ $isDummy }) => ($isDummy ? '9px 10px' : '7px 10px')};
  background-color: ${({ theme: { colors } }) => colors['bg-white']};
  ${fonts.Body2};
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
  cursor: pointer;
  &:hover {
    background-color: ${({ theme: { colors } }) => colors['bg-gray-lighter']};
  }
`;

const FileInfo = styled.div<{ $isDummy?: boolean }>`
  display: flex;
  align-items: center;
  min-width: 0px;
  opacity: ${({ $isDummy }) => ($isDummy ? 0.4 : 1)};
`;

const FileName = styled.div`
  margin-left: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
